// CollapsiblePanel — left-stack panel chrome with persisted collapse state.
// FR (drthe0, 2026-06-03).
//
// Reuses the existing panel surface (raised bg, subtle border, 16px
// padding) and adds a chevron-toggled header that persists open/closed
// state per panel under localStorage. Default-collapsed panels (e.g.
// Custom Properties) are honored only on first visit — once the
// operator opens or closes them by hand, that wins.

import { useEffect, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

export interface CollapsiblePanelProps {
  /** Stable identifier — used as the localStorage key suffix. Keep
   *  short and per-page-unique (e.g., 'docdetail:custom-properties'). */
  storageKey: string;
  title: string;
  /** Optional small text rendered after the title (counts, status). */
  badge?: ReactNode;
  /** If the localStorage key has never been written for this user,
   *  start collapsed. Otherwise honor the persisted value. */
  defaultCollapsed?: boolean;
  /** Default true. When false the open/closed state lives in memory only
   *  (resets on remount/navigation) and nothing is read from or written
   *  to localStorage. CTSK-522 — "Related" accordions reset per visit. */
  persist?: boolean;
  /** Optional right-aligned controls (edit icons, etc.). Rendered on
   *  the header row, doesn't react to the toggle click. */
  headerExtras?: ReactNode;
  children: ReactNode;
}

const LS_PREFIX = 'panel:collapsed:';

export function CollapsiblePanel({
  storageKey,
  title,
  badge,
  defaultCollapsed = false,
  persist = true,
  headerExtras,
  children,
}: CollapsiblePanelProps) {
  const lsKey = `${LS_PREFIX}${storageKey}`;

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (!persist || typeof window === 'undefined') return defaultCollapsed;
    const stored = window.localStorage.getItem(lsKey);
    if (stored === null) return defaultCollapsed;
    return stored === 'true';
  });

  useEffect(() => {
    if (!persist || typeof window === 'undefined') return;
    window.localStorage.setItem(lsKey, String(collapsed));
  }, [lsKey, collapsed, persist]);

  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        padding: 16,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: collapsed ? 0 : 12,
        }}
      >
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls={`panel-body-${storageKey}`}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
            color: 'var(--text-muted)',
            flex: 1,
            minWidth: 0,
            textAlign: 'left',
          }}
        >
          {collapsed
            ? <ChevronRight size={14} strokeWidth={2} aria-hidden />
            : <ChevronDown size={14} strokeWidth={2} aria-hidden />}
          <h3
            style={{
              margin: 0,
              fontSize: 14,
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '.06em',
              fontWeight: 600,
            }}
          >
            {title}
            {badge && (
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 11,
                  color: 'var(--text-muted)',
                  textTransform: 'lowercase',
                  fontWeight: 400,
                }}
              >
                {badge}
              </span>
            )}
          </h3>
        </button>
        {headerExtras && !collapsed && (
          <div style={{ display: 'inline-flex', alignItems: 'center' }}>
            {headerExtras}
          </div>
        )}
      </div>
      {!collapsed && (
        <div id={`panel-body-${storageKey}`}>
          {children}
        </div>
      )}
    </section>
  );
}
